import { Label, Lightbox, Skeleton, Stack, Text } from "@assetwatch/ui";
import { type SyntheticEvent, useRef, useState } from "react";
import type { WorkRequestId } from "../../../lib/workIds";
import {
  useWorkRequestFilesQuery,
  workRequestFilesStaleTime,
} from "../../../queries/useWorkRequestFiles";
import { WorkOrderAttachmentTile } from "../work-orders/WorkOrderAttachmentTile";

/** The request's attachments, including an empty state when no files exist. */
export function RequestAttachments({
  requestId,
}: {
  requestId: WorkRequestId;
}) {
  const query = useWorkRequestFilesQuery(requestId);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const refreshedForUpdate = useRef<number | null>(null);
  const tileButtons = useRef(new Map<string, HTMLButtonElement>());
  const files = query.data ?? [];

  function refreshExpiredUrls() {
    if (refreshedForUpdate.current === query.dataUpdatedAt) return;
    refreshedForUpdate.current = query.dataUpdatedAt;
    void query.refetch();
  }

  function openLightbox(index: number) {
    if (Date.now() - query.dataUpdatedAt > workRequestFilesStaleTime)
      void query.refetch();
    setLightboxIndex(index);
    setLightboxOpen(true);
  }

  function changeLightboxOpen(open: boolean) {
    setLightboxOpen(open);
    if (open) return;
    const fileId = files[lightboxIndex]?.id;
    const tile = fileId ? tileButtons.current.get(fileId) : undefined;
    if (tile) requestAnimationFrame(() => tile.focus());
  }

  function handleLightboxImageError(event: SyntheticEvent) {
    if (
      event.target instanceof Element &&
      event.target.closest('[data-slot="lightbox"]')
    )
      refreshExpiredUrls();
  }

  return (
    <div onError={handleLightboxImageError}>
      <Stack gap={8}>
        <Label size="sm" tone="subtle">
          ATTACHMENTS
        </Label>
        {query.isPending && files.length === 0 ? (
          <Skeleton height={20} width="100%" />
        ) : (
          <>
            {query.isError ? (
              <Text size="sm" tone="negative">
                Unable to load attachments.
              </Text>
            ) : null}
            {files.length > 0 ? (
              <div className="grid grid-cols-2 gap-(--spacing-12) sm:grid-cols-4">
                {files.map((file, index) => (
                  <WorkOrderAttachmentTile
                    canRemove={false}
                    file={file}
                    key={file.id}
                    onImageError={refreshExpiredUrls}
                    onOpen={() => openLightbox(index)}
                    openButtonRef={(element) => {
                      if (element) tileButtons.current.set(file.id, element);
                      else tileButtons.current.delete(file.id);
                    }}
                  />
                ))}
              </div>
            ) : !query.isError ? (
              <Text size="sm" tone="subtle">
                No files attached.
              </Text>
            ) : null}
          </>
        )}
      </Stack>
      <Lightbox
        aria-label="Request attachments"
        images={files.map((file) => ({
          src: file.downloadUrl,
          alt: file.fileName,
        }))}
        index={Math.min(lightboxIndex, Math.max(files.length - 1, 0))}
        onIndexChange={setLightboxIndex}
        onOpenChange={changeLightboxOpen}
        open={lightboxOpen && files.length > 0}
      />
    </div>
  );
}
